import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import api from '../utils/ApiManager';
import Navbar from '../components/Navbar';
import PricingEditor from '../components/PricingEditor';
import PromptEditor from '../components/PromptEditor';
import VideoGenerationConfigEditor from '../components/VideoGenerationConfigEditor';
import NotificationConfigEditor from '../components/NotificationConfigEditor';
import { theme } from '../utils/theme';
import RevenueOverview from '../components/dashboard/RevenueOverview';
import WorkerStats from '../components/dashboard/WorkerStats';
import ErrorLogs from '../components/dashboard/ErrorLogs';
import UserManagement from '../components/dashboard/UserManagement';
import VideoManagement from '../components/dashboard/VideoManagement';
import LumaGenerations from '../components/dashboard/LumaGenerations';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [errors, setErrors] = useState({ data: [], total: 0 });
    const [failedVideos, setFailedVideos] = useState([]);
    const [salesData, setSalesData] = useState(null);
    const [users, setUsers] = useState({ data: [], total: 0 });
    const [bucketStats, setBucketStats] = useState(null);
    const [userActivity, setUserActivity] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [currentPage, setCurrentPage] = useState({ errors: 1, users: 1, luma: 1 });
    const [workerStats, setWorkerStats] = useState(null);
    const [workerHistory, setWorkerHistory] = useState([]);
    const [lumaContent, setLumaContent] = useState([]);
    const [lumaContentLoading, setLumaContentLoading] = useState(false);
    const [workerStatsPage, setWorkerStatsPage] = useState(1);
    const [workerStatsPagination, setWorkerStatsPagination] = useState({
        total: 0,
        pages: 1,
        currentPage: 1,
        limit: 10
    });

    const fetchDashboardData = async () => {
        try {
            const [statsRes, salesRes, bucketStatsRes, activityRes] = await Promise.all([
                api.get('/api/admin/dashboard'),
                api.get('/api/admin/sales'),
                api.get('/api/admin/bucket-stats'),
                api.get('/api/admin/user-activity')
            ]);
            
            setStats(statsRes.data);
            setSalesData(salesRes.data);
            setBucketStats(bucketStatsRes.data);
            setUserActivity(activityRes.data);
            await fetchPaginatedData();
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const fetchPaginatedData = async () => {
        try {
            const [errorsRes, videosRes, usersRes] = await Promise.all([
                api.get(`/api/admin/errors?page=${currentPage.errors}&limit=10`),
                api.get('/api/admin/failed-videos'),
                api.get(`/api/admin/users?page=${currentPage.users}&limit=10`)
            ]);
            
            setErrors(errorsRes.data);
            setFailedVideos(videosRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            console.error('Error fetching paginated data:', error);
        }
    };

    const fetchWorkerStats = async () => {
        setLumaContentLoading(true);
        try {
            const response = await api.get(`/api/admin/worker-stats?page=${workerStatsPage}&limit=10`);
            setWorkerStats(response.data.current);
            setWorkerHistory(response.data.history || []);
            setWorkerStatsPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching worker stats:', error);
        } finally {
            setLumaContentLoading(false);
        }
    };

    const fetchLumaContent = async () => {
        setLumaContentLoading(true);
        try {
            const response = await api.get('/api/admin/luma-generations');
            setLumaContent(response.data);
        } catch (error) {
            console.error('Error fetching Luma content:', error);
        } finally {
            setLumaContentLoading(false);
        }
    };

    const handleDownloadLumaVideo = async (videoUrl) => {
        try {
            window.open(videoUrl, '_blank');
        } catch (error) {
            console.error('Error downloading video:', error);
        }
    };

    const handleDeleteLumaGeneration = async (generationId) => {
        try {
            await api.delete(`/api/admin/luma-generations/${generationId}`);
            fetchLumaContent();
        } catch (error) {
            console.error('Error deleting generation:', error);
        }
    };

    const handleDeleteAllLumaGenerations = async () => {
        if (window.confirm('Are you sure you want to delete all Luma generations? This action cannot be undone.')) {
            try {
                await api.delete('/api/admin/luma-generations');
                fetchLumaContent();
            } catch (error) {
                console.error('Error deleting all generations:', error);
            }
        }
    };

    const handleRetryGeneration = async (contentId) => {
        try {
            await api.post(`/api/admin/retry-generation/${contentId}`);
            fetchPaginatedData();
        } catch (error) {
            console.error('Error retrying video generation:', error);
        }
    };

    useEffect(() => {
        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 60000);
        fetchWorkerStats();
        const workerStatsInterval = setInterval(fetchWorkerStats, 60000);
        
        if (activeTab === 'luma') {
            fetchLumaContent();
        }
        
        return () => {
            clearInterval(interval);
            clearInterval(workerStatsInterval);
        };
    }, [activeTab, workerStatsPage]);

    useEffect(() => {
        fetchPaginatedData();
    }, [currentPage]);

    if (!stats) return (
        <div className={theme.page.default}>
            <Navbar />
            <div className="pt-20 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        </div>
    );

    return (
        <div className={theme.page.default}>
            <Navbar />
            <div className="fixed inset-0 pt-20 overflow-hidden">
                <div className={`${theme.container} h-full flex flex-col`}>
                    <div className="flex-none">
                        <h1 className={theme.text.h1}>Admin Dashboard</h1>
                        
                        <div className="flex gap-4 mb-6 overflow-x-auto py-2">
                            {['overview', 'workers', 'errors', 'videos', 'users', 'pricing', 'prompts', 'config', 'luma'].map(tab => (
                                <button 
                                    key={tab}
                                    className={`${theme.button.base} ${
                                        activeTab === tab ? theme.button.primary : theme.button.secondary
                                    } ${theme.button.sizes.sm} whitespace-nowrap`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto pb-6">
                        {activeTab === 'overview' && (
                            <RevenueOverview 
                                salesData={salesData} 
                                bucketStats={bucketStats} 
                                userActivity={userActivity}
                            />
                        )}

                        {activeTab === 'workers' && (
                            <WorkerStats 
                                workerStats={workerStats}
                                workerHistory={workerHistory}
                                workerStatsPagination={workerStatsPagination}
                                onPageChange={setWorkerStatsPage}
                            />
                        )}
                        
                        {activeTab === 'errors' && (
                            <ErrorLogs 
                                errors={errors}
                                currentPage={currentPage.errors}
                                onPageChange={(page) => setCurrentPage(prev => ({ ...prev, errors: page }))}
                            />
                        )}

                        {activeTab === 'users' && (
                            <UserManagement 
                                users={users}
                                currentPage={currentPage.users}
                                onPageChange={(page) => setCurrentPage(prev => ({ ...prev, users: page }))}
                            />
                        )}

                        {activeTab === 'videos' && (
                            <VideoManagement 
                                failedVideos={failedVideos}
                                onRetryGeneration={handleRetryGeneration}
                            />
                        )}

                        {activeTab === 'config' && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                <VideoGenerationConfigEditor />
                                <NotificationConfigEditor />
                            </div>
                        )}

                        {activeTab === 'pricing' && <PricingEditor />}
                        {activeTab === 'prompts' && <PromptEditor />}

                        {activeTab === 'luma' && (
                            <LumaGenerations 
                                lumaContent={lumaContent}
                                lumaContentLoading={lumaContentLoading}
                                onDownloadVideo={handleDownloadLumaVideo}
                                onDeleteGeneration={handleDeleteLumaGeneration}
                                onDeleteAll={handleDeleteAllLumaGenerations}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;